import React, { useState, useEffect } from 'react';
import { usePurchaseOrders } from '@/hooks/usePurchaseOrder';
import { useCreateInboundShipment } from '@/hooks/useInboundShipment';
import { POResponse } from '@/types/purchaseorder';
import { IQCStatus, IQC_STATUS_COLORS } from '@/types/inbound';
import { formatCurrency } from '@/services/poService';
import { toast } from 'react-hot-toast';

interface InboundShipmentDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ReceivedItem {
  line_no: number;
  product_name: string;
  specification: string;
  po_quantity: number;
  received_quantity: number;
  unit_price: number;
  currency: string;
  selected: boolean;
  iqc_status: IQCStatus;
  iqc_notes: string;
  remarks: string;
}

// Mocked barcode scan result type
interface BarcodeScanResult {
  barcode: string;
  timestamp: Date;
  matched: boolean;
  matchedItem?: string;
}

const InboundShipmentDialog: React.FC<InboundShipmentDialogProps> = ({
  onClose,
  onSuccess,
}) => {
  // Fetch all POs - we'll filter on frontend for receivable status
  const { data: poData, isLoading: poLoading } = usePurchaseOrders();
  const createMutation = useCreateInboundShipment();

  const [selectedPO, setSelectedPO] = useState<POResponse | null>(null);
  const [receivedDate, setReceivedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [receivedItems, setReceivedItems] = useState<ReceivedItem[]>([]);
  const [notes, setNotes] = useState('');
  const [showIQC, setShowIQC] = useState(true); // Show IQC by default

  // Barcode scanning state
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanHistory, setScanHistory] = useState<BarcodeScanResult[]>([]);
  const [isScanning, setIsScanning] = useState(false);

  // When PO is selected, populate the items list
  useEffect(() => {
    if (selectedPO) {
      const items: ReceivedItem[] = selectedPO.items
        .filter((item) => {
          // Include items that are not fully received and not cancelled
          const receivedQty = item.received_qty || 0;
          const remainingQty = item.quantity - receivedQty;
          return item.status !== 'received' && item.status !== 'cancelled' && remainingQty > 0;
        })
        .map((item) => {
          const receivedQty = item.received_qty || 0;
          const remainingQty = item.quantity - receivedQty;
          return {
            line_no: item.line_no,
            product_name: item.product_name,
            specification: item.specification,
            po_quantity: item.quantity,
            received_quantity: remainingQty, // Default to remaining qty
            unit_price: item.unit_price,
            currency: item.currency || selectedPO.currency,
            selected: true,
            iqc_status: 'pending' as IQCStatus,
            iqc_notes: '',
            remarks: '',
          };
        });
      setReceivedItems(items);
    } else {
      setReceivedItems([]);
    }
  }, [selectedPO]);

  const handlePOSelect = (poId: string) => {
    const po = poData?.purchase_orders?.find((p) => p.po_id === poId);
    setSelectedPO(po || null);
  };

  const handleItemToggle = (lineNo: number) => {
    setReceivedItems((prev) =>
      prev.map((item) =>
        item.line_no === lineNo ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleQuantityChange = (lineNo: number, quantity: number) => {
    setReceivedItems((prev) =>
      prev.map((item) =>
        item.line_no === lineNo
          ? { ...item, received_quantity: Math.max(0, quantity) }
          : item
      )
    );
  };

  const handleIQCStatusChange = (lineNo: number, status: IQCStatus) => {
    setReceivedItems((prev) =>
      prev.map((item) =>
        item.line_no === lineNo ? { ...item, iqc_status: status } : item
      )
    );
  };

  // Handle barcode scan (mocked)
  const handleBarcodeScan = (barcode: string) => {
    if (!barcode.trim()) return;

    // Simulate scanning delay
    setIsScanning(true);
    setTimeout(() => {
      // Try to match barcode with item (mock: match if barcode contains part of product name or spec)
      const matchedItem = receivedItems.find(
        (item) =>
          item.product_name.toLowerCase().includes(barcode.toLowerCase()) ||
          item.specification.toLowerCase().includes(barcode.toLowerCase()) ||
          barcode.toLowerCase().includes(item.product_name.substring(0, 5).toLowerCase())
      );

      const scanResult: BarcodeScanResult = {
        barcode,
        timestamp: new Date(),
        matched: !!matchedItem,
        matchedItem: matchedItem?.product_name,
      };

      setScanHistory((prev) => [scanResult, ...prev].slice(0, 10)); // Keep last 10 scans

      if (matchedItem) {
        // Select the item and increment quantity by 1
        setReceivedItems((prev) =>
          prev.map((item) =>
            item.line_no === matchedItem.line_no
              ? {
                  ...item,
                  selected: true,
                  received_quantity: Math.min(
                    item.received_quantity + 1,
                    item.po_quantity
                  ),
                }
              : item
          )
        );
        toast.success(`掃描成功: ${matchedItem.product_name}`);
      } else {
        toast.error(`無法識別條碼: ${barcode}`);
      }

      setBarcodeInput('');
      setIsScanning(false);
    }, 300); // Simulate 300ms scan delay
  };

  // Handle barcode input keydown (Enter to scan)
  const handleBarcodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeScan(barcodeInput);
    }
  };

  // Simulate camera scan (mock)
  const handleCameraScan = () => {
    // Mock: generate a random "scanned" barcode from available items
    if (receivedItems.length === 0) {
      toast.error('請先選擇採購單');
      return;
    }

    const randomItem = receivedItems[Math.floor(Math.random() * receivedItems.length)];
    const mockBarcode = `${randomItem.product_name.substring(0, 8)}-${Date.now().toString().slice(-4)}`;
    handleBarcodeScan(mockBarcode);
  };

  const handleSubmit = async () => {
    if (!selectedPO) {
      toast.error('請選擇採購單');
      return;
    }

    const selectedItems = receivedItems.filter(
      (item) => item.selected && item.received_quantity > 0
    );

    if (selectedItems.length === 0) {
      toast.error('請選擇至少一個進貨項目');
      return;
    }

    try {
      await createMutation.mutateAsync({
        po_id: selectedPO.po_id,
        received_date: receivedDate,
        items: selectedItems.map((item) => ({
          line_no: item.line_no,
          received_quantity: item.received_quantity,
          iqc_status: item.iqc_status,
          iqc_notes: item.iqc_notes,
          remarks: item.remarks,
        })),
        notes,
      });
      toast.success('進貨單已建立');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(
        `建立失敗: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const selectedItemsCount = receivedItems.filter((i) => i.selected).length;
  const totalAmount = receivedItems
    .filter((i) => i.selected)
    .reduce((sum, i) => sum + i.received_quantity * i.unit_price, 0);

  // Get available POs (ordered/approved status with items that can still receive goods)
  const availablePOs =
    poData?.purchase_orders?.filter(
      (po) =>
        // PO must be in a status that allows receiving goods
        (po.status === 'ordered' || po.status === 'approved') &&
        // Must have at least one item that is not fully received
        // Check if status is pending/partial, or if status is not set (treat as pending)
        po.items.some((item) =>
          !item.status ||
          item.status === 'pending' ||
          item.status === 'partial' ||
          (item.received_qty || 0) < item.quantity
        )
    ) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            新增進貨單
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* PO Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              選擇採購單 <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedPO?.po_id || ''}
              onChange={(e) => handlePOSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              disabled={poLoading}
            >
              <option value="">-- 請選擇採購單 --</option>
              {availablePOs.map((po) => (
                <option key={po.po_id} value={po.po_id}>
                  {po.po_number} - {po.vendor?.name || po.vendor_code} (
                  {formatCurrency(po.grand_total, po.currency)})
                </option>
              ))}
            </select>
            {poLoading && (
              <p className="mt-1 text-sm text-gray-500">載入中...</p>
            )}
            {!poLoading && availablePOs.length === 0 && (
              <p className="mt-1 text-sm text-yellow-600">
                目前沒有可進貨的採購單（需為已下單狀態）
              </p>
            )}
          </div>

          {/* Received Date */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              進貨日期 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          {/* Barcode Scanning Area */}
          {selectedPO && (
            <div className="mb-6 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                  />
                </svg>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  條碼掃描 (模擬)
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                  掃描或輸入條碼以快速登記進貨
                </span>
              </div>

              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyDown={handleBarcodeKeyDown}
                    placeholder="輸入或掃描條碼，按 Enter 確認..."
                    disabled={isScanning}
                    className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
                  />
                  <svg
                    className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {isScanning && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg
                        className="animate-spin h-5 w-5 text-blue-500"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleCameraScan}
                  disabled={isScanning || receivedItems.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  模擬掃描
                </button>
              </div>

              {/* Scan History */}
              {scanHistory.length > 0 && (
                <div className="mt-3 border-t border-gray-200 dark:border-gray-600 pt-3">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    掃描記錄 (最近 {scanHistory.length} 筆)
                  </div>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {scanHistory.map((scan, index) => (
                      <div
                        key={index}
                        className={`text-xs px-2 py-1 rounded flex items-center justify-between ${
                          scan.matched
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}
                      >
                        <span className="font-mono">{scan.barcode}</span>
                        <span>
                          {scan.matched ? (
                            <span className="flex items-center gap-1">
                              <svg
                                className="w-3 h-3"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {scan.matchedItem}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <svg
                                className="w-3 h-3"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              未識別
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Items Selection */}
          {selectedPO && receivedItems.length > 0 && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  進貨項目 <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowIQC(!showIQC)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showIQC ? '隱藏 IQC' : '顯示 IQC 檢驗'}
                </button>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-12">
                        選取
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        品名 / 規格
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-24">
                        訂購數量
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-32">
                        實收數量
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-28">
                        單價
                      </th>
                      {showIQC && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-32">
                          IQC 狀態
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {receivedItems.map((item) => (
                      <tr
                        key={item.line_no}
                        className={
                          item.selected
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : ''
                        }
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={item.selected}
                            onChange={() => handleItemToggle(item.line_no)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.product_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {item.specification}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                          {item.po_quantity}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input
                            type="number"
                            min="0"
                            max={item.po_quantity}
                            value={item.received_quantity}
                            onChange={(e) =>
                              handleQuantityChange(
                                item.line_no,
                                parseFloat(e.target.value) || 0
                              )
                            }
                            disabled={!item.selected}
                            className="w-24 px-2 py-1 text-right text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
                          />
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                          {formatCurrency(item.unit_price, item.currency)}
                        </td>
                        {showIQC && (
                          <td className="px-4 py-3">
                            <select
                              value={item.iqc_status}
                              onChange={(e) =>
                                handleIQCStatusChange(
                                  item.line_no,
                                  e.target.value as IQCStatus
                                )
                              }
                              disabled={!item.selected}
                              className={`px-2 py-1 text-xs font-semibold rounded border-0 ${
                                IQC_STATUS_COLORS[item.iqc_status]
                              } disabled:opacity-50`}
                            >
                              <option value="pending">待檢驗</option>
                              <option value="passed">合格</option>
                              <option value="failed">不合格</option>
                              <option value="waived">免檢</option>
                            </select>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              備註
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="進貨備註..."
            />
          </div>

          {/* Summary */}
          {selectedPO && selectedItemsCount > 0 && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  已選擇項目：
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {selectedItemsCount} 項
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-600 dark:text-gray-400">
                  進貨金額：
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(totalAmount, selectedPO.currency)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              !selectedPO ||
              selectedItemsCount === 0 ||
              createMutation.isLoading
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createMutation.isLoading ? '建立中...' : '建立進貨單'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InboundShipmentDialog;
